package com.dignicate.web.ui.components.header

import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.dignicate.web.css.Contain
import com.dignicate.web.css.Cursor
import com.dignicate.web.css.Isolation
import com.dignicate.web.css.JustifyContent
import com.dignicate.web.css.PointerEvents
import com.dignicate.web.css.TransitionProperty
import com.dignicate.web.css.WillChange
import com.dignicate.web.css.WidthValue
import com.dignicate.web.css.background
import com.dignicate.web.css.contain
import com.dignicate.web.css.cursor
import com.dignicate.web.css.gap
import com.dignicate.web.css.gridTemplateColumns
import com.dignicate.web.css.height
import com.dignicate.web.css.inset
import com.dignicate.web.css.isolation
import com.dignicate.web.css.justifyContent
import com.dignicate.web.css.pointerEvents
import com.dignicate.web.css.rgbaColor
import com.dignicate.web.css.right
import com.dignicate.web.css.s
import com.dignicate.web.css.top
import com.dignicate.web.css.transformOrigin
import com.dignicate.web.css.transition
import com.dignicate.web.css.translateZ
import com.dignicate.web.css.width
import com.dignicate.web.css.willChange
import com.dignicate.web.css.zIndex
import com.dignicate.web.theme.LocalIsDark
import com.dignicate.web.ui.util.navigateAndScrollTop
import kotlinx.browser.document
import kotlinx.browser.window
import org.jetbrains.compose.web.css.AlignItems
import org.jetbrains.compose.web.css.DisplayStyle
import org.jetbrains.compose.web.css.Position
import org.jetbrains.compose.web.css.alignItems
import org.jetbrains.compose.web.css.display
import org.jetbrains.compose.web.css.padding
import org.jetbrains.compose.web.css.position
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Img
import org.w3c.dom.Node

private class TurbulenceAnimator(val turbulenceId: String) {
    var rafId: Int? = null
    var t: Double = 0.0

    fun start() {
        rafId = window.requestAnimationFrame { timestamp -> tick(timestamp) }
    }

    private fun tick(_timestamp: Double) {
        t += 0.00025
        val bfX = 0.05 + 0.004 * kotlin.math.sin(t)
        val bfY = 0.045 + 0.004 * kotlin.math.cos(t)
        val turb = document.getElementById(turbulenceId)
        val bfStr = "$bfX $bfY"
        turb?.setAttribute("baseFrequency", bfStr)
        rafId = window.requestAnimationFrame { ts -> tick(ts) }
    }

    fun stop() {
        rafId?.let { window.cancelAnimationFrame(it) }
    }
}

@Composable
fun Header(navigate: (String) -> Unit, onOpenSettings: () -> Unit) {
    // theme
    val isDark = LocalIsDark.current

    // --- スクロールでshow/hide制御用state ---
    val prefersReducedMotion = remember {
        try {
            window.matchMedia("(prefers-reduced-motion: reduce)").matches
        } catch (_: Throwable) { false }
    }
    var isHeaderHidden by remember { mutableStateOf(false) }
    var hoverReveal by remember { mutableStateOf(false) }
    val visible = !isHeaderHidden || hoverReveal
    // DisposableEffectでイベント登録・解除
    DisposableEffect(prefersReducedMotion) {
        if (prefersReducedMotion) {
            isHeaderHidden = false
            hoverReveal = false
            onDispose { }
        } else {
            val topSafePx = 160.0
            val hideThreshold = 28.0
            val showThreshold = 8.0
            var lastScrollY = window.scrollY
            var accDown = 0.0
            var accUp = 0.0
            var ticking = false
            fun onScroll() {
                val currentY = window.scrollY
                val delta = currentY - lastScrollY
                lastScrollY = currentY
                if (!ticking) {
                    ticking = true
                    window.requestAnimationFrame {
                        if (currentY <= topSafePx) {
                            if (isHeaderHidden) isHeaderHidden = false
                            accDown = 0.0
                            accUp = 0.0
                            ticking = false
                            hoverReveal = false // スクロールでvisibleになったらhoverReveal解除
                            return@requestAnimationFrame
                        }
                        if (delta > 0) {
                            accDown += delta
                            accUp = 0.0
                            if (accDown > hideThreshold && !isHeaderHidden) {
                                isHeaderHidden = true
                            }
                        } else if (delta < 0) {
                            accUp -= delta
                            accDown = 0.0
                            if (accUp > showThreshold && isHeaderHidden) {
                                isHeaderHidden = false
                                hoverReveal = false // スクロールでvisibleになったらhoverReveal解除
                            } else if (accUp > 0.0 && !isHeaderHidden) {
                                isHeaderHidden = false
                                hoverReveal = false // スクロールでvisibleになったらhoverReveal解除
                            }
                        }
                        ticking = false
                    }
                }
            }
            val listener: (dynamic) -> Unit = { onScroll() }
            window.addEventListener("scroll", listener)
            onDispose {
                window.removeEventListener("scroll", listener)
            }
        }
    }
    // --- hoverReveal: マウス上端ホバー検知 ---
    DisposableEffect(isHeaderHidden) {
        if (isHeaderHidden) {
            val mouseMoveListener: (dynamic) -> Unit = { event ->
                val clientY = (event as? org.w3c.dom.events.MouseEvent)?.clientY?.toDouble()
                if (clientY != null && clientY <= 120.0) {
                    hoverReveal = true
                }
            }
            window.addEventListener("mousemove", mouseMoveListener)
            onDispose {
                window.removeEventListener("mousemove", mouseMoveListener)
            }
        } else {
            hoverReveal = false
            onDispose { }
        }
    }
    // --- ここまで ---

    // NOTE: replaced randomized SVG-injection with a single persistent SVG + animated feTurbulence (方式B2)
    DisposableEffect(Unit) {
        val svgId = "header-glass-svg"
        val filterId = "header-glass-filter"
        val turbulenceId = "header-turbulence"

        var injected = false
        var containerNode: Node? = null
        // If not already present in the DOM, inject the SVG definitions once
        if (document.getElementById(svgId) == null) {
            val svgString = """
            <svg id="$svgId" xmlns="http://www.w3.org/2000/svg" width="0" height="0" style="position:absolute;width:0;height:0;pointer-events:none;">
              <defs>
                <filter id="$filterId" x="-20%" y="-20%" width="140%" height="140%">
                  <!-- Turbulence -> posterize -> contrast -> blend to create visible glass texture -->
                  <feTurbulence id="$turbulenceId" type="fractalNoise" baseFrequency="0.05 0.045" numOctaves="2" seed="2" result="noise"/>
                  <feColorMatrix in="noise" type="saturate" values="0" result="gray"/>
                  <feComponentTransfer in="gray" result="posterized">
                    <feFuncR type="discrete" tableValues="0.2 0.5 0.8"/>
                    <feFuncG type="discrete" tableValues="0.2 0.5 0.8"/>
                    <feFuncB type="discrete" tableValues="0.2 0.5 0.8"/>
                  </feComponentTransfer>
                  <feComponentTransfer in="posterized" result="contrast">
                    <feFuncR type="linear" slope="1.6" intercept="-0.1"/>
                    <feFuncG type="linear" slope="1.6" intercept="-0.1"/>
                    <feFuncB type="linear" slope="1.6" intercept="-0.1"/>
                    <feFuncA type="linear" slope="0.5"/>
                  </feComponentTransfer>
                  <feDisplacementMap in="SourceGraphic" in2="noise" scale="14" xChannelSelector="R" yChannelSelector="G"/>
                  <feBlend in="SourceGraphic" in2="contrast" mode="overlay"/>
                </filter>
              </defs>
            </svg>
            """.trimIndent()

            val container = document.createElement("div").apply {
                innerHTML = svgString
                setAttribute("style", "position:absolute;width:0;height:0;overflow:hidden;pointer-events:none;")
            }
            document.body?.appendChild(container)
            injected = true
            containerNode = container
        }

        // Respect prefers-reduced-motion
        val reduced = try {
            window.matchMedia("(prefers-reduced-motion: reduce)").matches
        } catch (_: Throwable) {
            false
        }

        val animator = if (!reduced) TurbulenceAnimator(turbulenceId).apply { start() } else null

        onDispose {
            animator?.stop()
            // remove injected SVG container only if we created it here
            if (injected) {
                try {
                    if (containerNode != null && containerNode.parentNode != null) {
                        containerNode.parentNode?.removeChild(containerNode)
                    } else {
                        val el = document.getElementById(svgId)
                        if (el != null && el.parentNode != null) el.parentNode?.removeChild(el)
                    }
                } catch (_: Throwable) {}
            }
        }
    }

    // Outer fixed wrapper: position: fixed, top: 0, left: 0, right: 0, z-index: 1000
    Div({ classes("site-header");
        if (!visible && !prefersReducedMotion) classes("is-hidden")
        style {
            position(Position.Fixed)
            top(0.px)
            property("left", "0")
            right(0.px)
            zIndex(1000)
            if (isDark) background(rgbaColor(10, 10, 12, 0.2)) else background(rgbaColor(255, 255, 255, 0.2))
            cursor(Cursor.Default)
        }
        // hoverRevealで表示中かつscrollVisible=falseのときmouseleaveでhoverReveal=false
        if (hoverReveal && isHeaderHidden) {
            onMouseLeave { hoverReveal = false }
        }
    }) {
        // 1) Background layer (bgLayer): Canvas/SVG pattern. This layer may be pre-blurred in non-WebKit browsers via CSS.
        // bgLayer is the element that receives compositing/transform and (in non-WebKit) the SVG filter via stylesheet (@supports not (-webkit-backdrop-filter)).
        // Per requirement, blur/filter and transform are consolidated onto this same element to force GPU compositing on Chrome/Firefox.
        Div({ classes("header-bg-layer"); style {
            position(Position.Absolute)
            inset(0.px)
            zIndex(0)
            pointerEvents(PointerEvents.None)
            translateZ(0.px)
            willChange(WillChange.Transform, WillChange.Opacity, WillChange.Filter)
            property("backface-visibility", "hidden")
            contain(Contain.Paint)
            transformOrigin("center")
        } }) {
            HeaderWavyPatternCanvas(
                isDark = isDark,
                enabled = true,
                intensity = 0.75f,
                speed = 0.6f,
                heightPx = 120
            )
        }

        // 2) Dedicated blur/tint overlay (header-blur-overlay): used as a tint over the blurred bg-layer in non-WebKit; hidden in WebKit via CSS @supports so Safari is unchanged.
        Div({ classes("header-blur-overlay"); style {
            position(Position.Absolute)
            inset(0.px)
            zIndex(1)
            pointerEvents(PointerEvents.None)
            // theme-aware background is set inline in Header.kt (keeps Safari tint identical)
            if (isDark) background(rgbaColor(10, 10, 12, 0.2)) else background(rgbaColor(255, 255, 255, 0.2))
            willChange(WillChange.Transform, WillChange.Opacity)
            translateZ(0.px)
            property("backface-visibility", "hidden")
            contain(Contain.Paint)
            isolation(Isolation.Isolate)
        } }) { /* purely visual overlay - shown in non-WebKit by stylesheet */ }

        // 3) Foreground/content layer: interactive elements that should NOT be affected by blur
        Div({ classes("header-content-layer"); style { display(DisplayStyle.Grid); gridTemplateColumns("1fr auto"); alignItems(AlignItems.Center); padding(0.px, 20.px); property("min-height", "120px"); height(120.px); position(Position.Relative); zIndex(2); willChange(WillChange.Transform, WillChange.Opacity) } }) {
            // Left: Logo
            Div({ style { display(DisplayStyle.Flex); alignItems(AlignItems.Center); padding(16.px) } }) {
                Img(src = if (isDark) "/assets/logo/dignicate-horizontal-white.png" else "/assets/logo/dignicate-horizontal-black.png", attrs = {
                    attr("alt", "Dignicate")
                    style { height(96.px); width(WidthValue.Auto); property("max-height", "none"); cursor(Cursor.Pointer) }
                    onClick {
                        // always navigate to top and reset scroll position
                        navigateAndScrollTop(
                            navigate,
                            "/"
                        )
                    }
                })
            }

            // Right area: settings (mini nav removed)
            Div({ style { property("justify-self", "end"); display(DisplayStyle.Flex); alignItems(AlignItems.Center); gap(12.px) } }) {
                // Settings button wrapper
                Div({ style { width(44.px); height(44.px); display(DisplayStyle.Flex); alignItems(AlignItems.Center); justifyContent(JustifyContent.Center); property("border-radius", "10px"); transition { t(TransitionProperty.BACKGROUND_COLOR, duration = s(0.12)) } } }) {
                    SettingsButton(onClick = onOpenSettings)
                }
            }
        }
    }
}
